import React, { RefObject, useCallback, useEffect, useMemo, useRef, useState, JSX } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendar, faTimesCircle } from '@fortawesome/free-regular-svg-icons';
import { Button, Input, Modal, ModalBody, Popover, PopoverProps } from 'reactstrap';
import DatePicker from '../DatePicker/DatePicker';
import { getMonthIndex, getYear } from '../DatePicker/useAdvancedFeatures';

import useConfig from '../../../Hooks/useConfig';
import fallback from '../../../../_Translations/generated/date-range-single-field.en.t.json';
import Keys from '../../../../_Translations/generated/date-range-single-field.en.t.json.keys';
import useTranslation from '../../../Hooks/useTranslation';
import useAppService from '../../../Hooks/useAppService';
import { DateRangeDurationPropsType } from '../../../SubModules/PackageSearch/PackageSearch';
import {
  DefaultDate,
  getExcludedDaysInCurrentMonth,
  Mode,
  getMinDate
} from './DateRangeInputSingleFieldHelpers';
import useMediaQuery, { MEDIAQUERY_DEFAULTS } from '../../../Hooks/useMediaQuery';
import { AppService, ConfigModel, createUniqueId } from '@ibe/services';
import useWindow from '../../../Hooks/useWindow';

import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import CustomContainer from './CustomContainer';
import { useClickAway } from 'react-use';

dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

export const getFormattedDate = (
  config: ConfigModel,
  appService: AppService,
  date?: Date | string
): string | undefined => {
  return !!date ? dayjs(date).format(config.displayFormatDate[appService.lang]) : undefined;
};
const isValidStartDate = (startDate: string | undefined, format: string) => {
  if (!startDate) {
    return false;
  }

  const newStartDate = dayjs(startDate, format, true).startOf('day');
  const today = dayjs().startOf('day');
  // must be a valid date & at least the current date
  return newStartDate.isValid() && newStartDate.isSameOrAfter(today);
};
const isValidEndDate = (endDate: string | undefined, format: string) => {
  if (!endDate) {
    return false;
  }

  const newEndDate = dayjs(endDate, format, true);
  // must be a valid date & after the current date
  return newEndDate.isValid() && newEndDate.isAfter(new Date());
};
const keyboardArrowKeys = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'];

export interface Props {
  containerRef: RefObject<HTMLDivElement>;
  startDate?: string;
  endDate?: string;
  setStartDate: (startDate: string) => void;
  setEndDate: (startDate: string) => void;
  startDateProp?: string | null;
  endDateProp?: string | null;
  mode: Mode;
  setMode: (mode: Mode) => void;
  defaultDate?: DefaultDate;
  minDate?: string;
  maxDate?: string;
  setLocalDuration: (localDuration?: number) => void;
  localDuration?: number;
  buttonRef: RefObject<HTMLButtonElement>;
  handleInputClick: (inputMode: Mode) => void;
  popoverOpen: boolean;
  setPopoverOpen: (popoverOpen: boolean) => void;
  togglePopover: () => void;
  popoverPlacement?: PopoverProps['placement'];
  disabled?: boolean;
  durationProp?: DateRangeDurationPropsType;
  handleSubmit: (validStartDate: string, validEndDate: string) => void;
  alternativeDateLabels?: {
    startDate: string;
    endDate: string;
    duration: string;
  };
  minDateRangeSpan?: number;
  maxDateRangeSpan?: number;
  submitOnLeave?: boolean;
}

const PopoverContent = (
  props: Props & {
    currentMonth: number;
    setCurrentMonth: (currentMonth: number) => void;
    currentYear: number;
    setCurrentYear: (currentYear: number) => void;
  }
): JSX.Element => {
  const {
    startDate,
    endDate,
    setStartDate,
    setEndDate,
    startDateProp,
    endDateProp,
    mode,
    setMode,
    defaultDate,
    minDate,
    setLocalDuration,
    popoverOpen,
    localDuration,
    maxDate,
    durationProp,
    handleSubmit,
    alternativeDateLabels,
    minDateRangeSpan,
    maxDateRangeSpan,
    currentYear,
    currentMonth,
    setCurrentYear,
    setCurrentMonth,
    containerRef,
    togglePopover,
    submitOnLeave
  } = props;
  const { t } = useTranslation('date-range-single-field', fallback, Keys);
  const appService = useAppService();
  const config = useConfig();
  const dateFormat = config.formatDate;
  const displayFormat = config.displayFormatDate[appService.lang];
  const window = useWindow();

  const datePickerWrapperRef = useRef<HTMLDivElement>(null);

  const popoverRef = useRef<HTMLDivElement>(null);
  const oldEndDate = useRef<string | null | undefined>(endDate);
  const oldStartDate = useRef<string | null | undefined>(startDate);
  const oldPressedKeyBoardKey = useRef<string | null>(null);
  const [calendarKeys, setCalendarKeys] = useState<string>(createUniqueId());
  const [selectedDate, setSelectedDate] = useState<string | null | undefined>(startDate);
  const isDesktop = useMediaQuery({ type: 'min', query: MEDIAQUERY_DEFAULTS.md });

  useClickAway(containerRef, (): void => {
    if (isDesktop) {
      togglePopover();
    }
  });

  const excludeDates = useMemo(() => {
    return getExcludedDaysInCurrentMonth(
      currentMonth,
      currentYear,
      mode,
      dayjs(oldStartDate.current, displayFormat).toDate(),
      localDuration,
      minDate,
      defaultDate
    );
  }, [currentMonth, currentYear, mode, localDuration, oldStartDate.current, minDate, defaultDate]);

  useEffect(() => {
    setCurrentMonth(dayjs(startDateProp || defaultDate?.startDate || new Date()).get('month'));
    setCurrentYear(dayjs(startDateProp || defaultDate?.startDate || new Date()).get('year'));
  }, [startDateProp, defaultDate]);

  useEffect(() => {
    if (popoverOpen) {
      setTimeout(() => {
        handleCalendarOpen();
      }, 200);
    }
  }, [popoverOpen]);

  const checkButtonDisable = () => {
    return (
      !oldStartDate.current ||
      !oldEndDate.current ||
      dayjs(oldEndDate.current, displayFormat).isSameOrBefore(
        dayjs(oldStartDate.current, displayFormat)
      )
    );
  };

  const submit = () => {
    if (oldEndDate.current && oldStartDate.current) {
      handleSubmit(oldStartDate.current, oldEndDate.current);
    }
  };

  const isButtonDisable = checkButtonDisable();

  useEffect(() => {
    if (submitOnLeave && !popoverOpen && !isButtonDisable) {
      submit();
    }
  }, [popoverOpen, submitOnLeave]);

  useEffect(() => {
    const newDate = dayjs(startDate, displayFormat);
    if (isValidStartDate(startDate, displayFormat) && checkIsInsideDateRange(newDate)) {
      oldStartDate.current = startDate as string;
      setSelectedDate(startDate);
      setCalendarKeys(createUniqueId());
    }
  }, [startDate]);

  useEffect(() => {
    const newDate = dayjs(endDate, displayFormat);
    if (isValidEndDate(endDate, displayFormat) && checkIsInsideDateRange(newDate)) {
      oldEndDate.current = endDate as string;
      setSelectedDate(endDate);
      setCalendarKeys(createUniqueId());
    }
  }, [endDate]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (mode !== 'selectsStart' && mode !== 'selectsEnd') return;
      let newDate = dayjs(selectedDate, displayFormat);
      if (keyboardArrowKeys.includes(event.key)) {
        event.preventDefault();
        if (mode === 'selectsEnd') {
          if (!isValidEndDate(endDate, displayFormat)) {
            setEndDate('');
          }
        } else if (mode === 'selectsStart') {
          if (!isValidStartDate(startDate, displayFormat)) {
            setStartDate('');
          }
        }
      }

      switch (event.key) {
        case keyboardArrowKeys[0]:
          newDate = newDate.subtract(1, 'days');
          break;
        case keyboardArrowKeys[1]:
          newDate = newDate.add(1, 'days');
          break;
        case keyboardArrowKeys[2]:
          newDate = newDate.subtract(7, 'days');
          break;
        case keyboardArrowKeys[3]:
          newDate = newDate.add(7, 'days');
          break;
        case 'Enter':
        case 'Escape':
          // this aditional check is necessary to ensure Enter and Escape behaving the same,
          // because otherwise the Escape-Event behaves differently from the Enter-Event
          if (mode === 'selectsStart') {
            if (!checkIsInsideDateRange(dayjs(startDate, displayFormat))) {
              if (oldStartDate.current) {
                setStartDate(oldStartDate.current);
              }
            }
          } else if (mode === 'selectsEnd') {
            if (!checkIsInsideDateRange(dayjs(endDate, displayFormat))) {
              if (oldEndDate.current) {
                setEndDate(oldEndDate.current);
              }
            }
          }

          if (!selectedDate) break;
          if (keyboardArrowKeys.includes(oldPressedKeyBoardKey?.current || '')) {
            const findDateIntoExcludesDate = excludeDates.find(
              excl => dayjs(excl).format(displayFormat) === selectedDate
            );
            if (findDateIntoExcludesDate) return;
            if (mode === 'selectsStart') {
              setStartDate(selectedDate);
            } else if (mode === 'selectsEnd') {
              setEndDate(selectedDate);
            }
          } else {
            if (mode === 'selectsEnd') {
              if (isValidEndDate(endDate, displayFormat) && oldEndDate.current) {
                setEndDate(oldEndDate.current);
              }
            } else if (mode === 'selectsStart') {
              if (!isValidStartDate(startDate, displayFormat) && oldStartDate.current) {
                setStartDate(oldStartDate.current);
              }
            }
          }
          setMode('none');
          break;
        default:
          return;
      }

      if (checkIsInsideDateRange(newDate)) {
        setSelectedDate(dayjs(newDate).format(displayFormat));
        setCalendarKeys(createUniqueId());
      }
      oldPressedKeyBoardKey.current = event.key;
    };
    window?.addEventListener('keydown', handleKeyDown);
    return () => {
      window?.removeEventListener('keydown', handleKeyDown);
    };
  }, [
    selectedDate,
    startDate,
    endDate,
    oldStartDate.current,
    oldEndDate.current,
    oldPressedKeyBoardKey.current,
    excludeDates
  ]);

  const handleDurationChange = useCallback(
    (numberOfNights?: number) => {
      if (endDate && startDate && numberOfNights) {
        const diff = dayjs(endDate).diff(dayjs(startDate), 'day');
        if (numberOfNights > diff) {
          const updatedDate = dayjs(startDate).add(numberOfNights, 'day').format(displayFormat);
          setEndDate(updatedDate);
        }
      }
      setLocalDuration(numberOfNights);
    },
    [endDateProp, startDateProp, dateFormat, endDate, startDate]
  );

  const handleDateChange = useCallback(
    (date: Date): void => {
      if (mode === 'selectsStart') {
        setStartDate(dayjs(date).format(displayFormat));
        if (!!endDate && dayjs(date).add(1, 'day').isAfter(dayjs(endDate))) {
          setEndDate(
            dayjs(date)
              .add(localDuration || 1, 'day')
              .format(displayFormat)
          );
        }
      } else if (mode === 'selectsEnd') {
        setEndDate(dayjs(date).format(displayFormat));
      }
      setMode('none');
    },
    [mode]
  );

  const checkIsInsideDateRange = (newDate: dayjs.Dayjs): boolean => {
    if (!minDate || !maxDate) return false;

    const newDateUTC = dayjs(newDate).startOf('day');
    const minDateUTC = dayjs(minDate).startOf('day');
    const maxDateUTC = dayjs(maxDate).startOf('day');

    return newDateUTC.isSameOrAfter(minDateUTC) && newDateUTC.isSameOrBefore(maxDateUTC);
  };

  const handleCalendarOpen = (): void => {
    if (!!datePickerWrapperRef.current) {
      const month = datePickerWrapperRef.current.querySelector(
        '.react-datepicker__month-container'
      );
      if (!!month) {
        setCurrentMonth(getMonthIndex(month));
        setCurrentYear(getYear(month));
      }
    }
    if (mode === 'selectsEnd') {
      setSelectedDate(oldEndDate.current);
      setCalendarKeys(createUniqueId());
    } else if (mode === 'selectsStart') {
      setSelectedDate(oldStartDate.current);
      setCalendarKeys(createUniqueId());
    }
  };

  const handleMonthChange = (newDate: Date): void => {
    setCurrentMonth(dayjs(newDate).get('month'));
    const firstDayElement = document.querySelector('.react-datepicker__day--001') as HTMLDivElement;
    firstDayElement.focus();
  };

  const handleYearChange = (newDate: Date): void => {
    setCurrentYear(dayjs(newDate).get('year'));
  };

  const getDayClass = (date: Date): string => {
    if (
      (!!startDate &&
        dayjs(startDate, displayFormat).date() === dayjs(date).date() &&
        (dayjs(startDate, displayFormat).isBefore(dayjs(date), 'day') ||
          dayjs(startDate, displayFormat).isAfter(dayjs(date), 'day'))) ||
      (!!endDate &&
        dayjs(endDate, displayFormat).date() === dayjs(date).date() &&
        (dayjs(endDate, displayFormat).isBefore(dayjs(date), 'day') ||
          dayjs(endDate, displayFormat).isAfter(dayjs(date), 'day')))
    ) {
      return 'date-range-single__same-day';
    } else {
      return '';
    }
  };

  const getFilterDate = (date: Date): boolean => {
    if (!maxDateRangeSpan && !minDateRangeSpan) {
      return true;
    }
    if (!!maxDateRangeSpan) {
      const start = dayjs(startDate, displayFormat)
        .subtract(1, 'day')
        .add(minDateRangeSpan ?? 0, 'day');
      const end = dayjs(startDate, displayFormat).add(maxDateRangeSpan + 1, 'day');
      const current = dayjs(date);
      return (current.isAfter(start) && current.isBefore(end)) || !endDate;
    }
    if (!!minDateRangeSpan) {
      const start = dayjs(startDate, displayFormat).subtract(1, 'day');
      const end = dayjs(startDate, displayFormat).subtract(1, 'day').add(minDateRangeSpan, 'day');
      const current = dayjs(date);
      return !current.isAfter(start) || current.isAfter(end) || !endDate;
    }
    return true;
  };

  return (
    <div ref={popoverRef} className="date-range-single__popover__inner">
      <div className="date-range-single__input__container d-flex">
        <div
          className={`date-range-single__input${
            mode === 'selectsStart' ? ' date-range-single__input--selected' : ''
          }`}
        >
          <label htmlFor="startDate__input">
            {alternativeDateLabels?.startDate || t(Keys.earliestTravelDate)}
          </label>
          <FontAwesomeIcon icon={faCalendar} />
          <Input
            id="startDate__input"
            value={startDate}
            onChange={e => {
              const newDate = e.target.value;
              setStartDate(newDate);
            }}
            onFocus={() => {
              setMode('selectsStart');
              setSelectedDate(oldStartDate.current);
            }}
            onKeyDown={e => {
              if (keyboardArrowKeys.includes(e.key)) {
                return e.preventDefault();
              }
            }}
            onBlur={e => {
              const newDate = dayjs(e.target.value, displayFormat);
              if (
                isValidStartDate(e.target.value, displayFormat) &&
                checkIsInsideDateRange(newDate)
              ) {
                return setStartDate(e.target.value);
              } else if (oldStartDate.current) {
                return setStartDate(oldStartDate.current);
              }
            }}
          />
        </div>
        <div
          className={`date-range-single__input${
            mode === 'selectsEnd' ? ' date-range-single__input--selected' : ''
          }`}
        >
          <label htmlFor="endDate__input">
            {alternativeDateLabels?.endDate || t(Keys.latestTravelDate)}
          </label>
          <FontAwesomeIcon icon={faCalendar} />
          <Input
            id="endDate__input"
            value={endDate}
            onChange={e => {
              const newDate = e.target.value;
              setEndDate(newDate);
            }}
            onFocus={() => {
              setMode('selectsEnd');
              setSelectedDate(oldEndDate.current);
            }}
            onKeyDown={e => {
              if (keyboardArrowKeys.includes(e.key)) {
                return e.preventDefault();
              }
            }}
            onBlur={e => {
              const newDate = dayjs(e.target.value, displayFormat);
              if (
                isValidEndDate(e.target.value, displayFormat) &&
                checkIsInsideDateRange(newDate)
              ) {
                return setEndDate(e.target.value);
              } else if (oldEndDate.current) {
                return setEndDate(oldEndDate.current);
              }
            }}
          />
        </div>
      </div>
      <div ref={datePickerWrapperRef} className="date-range-single__datepicker">
        {(mode === 'selectsStart' || mode === 'selectsEnd') &&
          oldEndDate.current &&
          oldStartDate.current && (
            <DatePicker
              dataTestId="date-range-single-field"
              key={calendarKeys}
              inline
              onChange={
                handleDateChange as (
                  date: Date | null,
                  event?:
                    | React.MouseEvent<HTMLElement>
                    | React.KeyboardEvent<HTMLElement>
                    | undefined
                ) => void
              }
              selectsStart={mode === 'selectsStart'}
              selectsEnd={mode === 'selectsEnd'}
              startDate={
                !!oldStartDate.current
                  ? dayjs(oldStartDate.current, displayFormat).toDate()
                  : undefined
              }
              endDate={
                !!oldEndDate.current ? dayjs(oldEndDate.current, displayFormat).toDate() : undefined
              }
              selected={dayjs(selectedDate, displayFormat).toDate()}
              minDate={getMinDate(
                mode,
                dayjs(oldStartDate.current, displayFormat).toDate(),
                minDate,
                defaultDate
              )}
              maxDate={!!maxDate ? new Date(maxDate) : undefined}
              onCalendarOpen={handleCalendarOpen}
              onMonthChange={handleMonthChange}
              onYearChange={handleYearChange}
              excludeDates={excludeDates}
              openToDate={
                selectedDate
                  ? dayjs(selectedDate, displayFormat).toDate()
                  : mode === 'selectsEnd' && !!oldEndDate.current
                  ? dayjs(oldEndDate.current, displayFormat).toDate()
                  : !!startDate
                  ? dayjs(oldStartDate.current, displayFormat).toDate()
                  : new Date()
              }
              dayClassName={getDayClass}
              filterDate={mode === 'selectsEnd' ? getFilterDate : undefined}
              highlightDates={[dayjs(selectedDate, displayFormat).toDate()]}
            />
          )}
      </div>
      {durationProp?.withDuration && (
        <div className="date-range-single__custom-container">
          <label>{alternativeDateLabels?.duration || t(Keys.duration)}</label>
          <CustomContainer
            handleDurationChange={handleDurationChange}
            asSimpleElement
            duration={localDuration || durationProp?.value?.duration}
          />
        </div>
      )}
      <Button
        className="date-range-single__confirm w-100"
        color="primary"
        disabled={isButtonDisable}
        onClick={submit}
      >
        {t(Keys.confirm)}
      </Button>
    </div>
  );
};

const DateRangeInputSingleFieldPopover = (props: Props): JSX.Element => {
  const {
    containerRef,
    buttonRef,
    popoverOpen,
    setPopoverOpen,
    togglePopover,
    disabled,
    popoverPlacement
  } = props;
  const matches = useMediaQuery({ type: 'min', query: MEDIAQUERY_DEFAULTS.md });

  const [currentMonth, setCurrentMonth] = useState<number>(0);
  const [currentYear, setCurrentYear] = useState<number>(0);

  const handlePopoverClick = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    e.preventDefault();
  };

  return matches ? (
    <Popover
      onClick={handlePopoverClick}
      className="date-range-single__popover"
      container={containerRef}
      target={buttonRef}
      isOpen={popoverOpen}
      placement={popoverPlacement || 'bottom'}
      disabled={disabled}
      trigger="legacy"
    >
      <PopoverContent
        {...props}
        currentMonth={currentMonth}
        currentYear={currentYear}
        setCurrentYear={setCurrentYear}
        setCurrentMonth={setCurrentMonth}
      />
    </Popover>
  ) : (
    <Modal
      className="date-range-single__modal"
      container={document.getElementById('iso') || undefined}
      isOpen={popoverOpen}
      toggle={togglePopover}
    >
      <ModalBody>
        <FontAwesomeIcon
          icon={faTimesCircle}
          className="date-range-single__close"
          onClick={(): void => setPopoverOpen(false)}
        />
        <PopoverContent
          {...props}
          currentMonth={currentMonth}
          currentYear={currentYear}
          setCurrentYear={setCurrentYear}
          setCurrentMonth={setCurrentMonth}
        />
      </ModalBody>
    </Modal>
  );
};

export default DateRangeInputSingleFieldPopover;
